import { Form, Input, InputNumber, Switch } from "antd";
import type { CSSProperties } from "react";
import { AwsRegionSelect } from "@/common/aws";
import type {
	PlaceholderField,
	PlaceholderValue,
	PlaceholderValueType,
} from "@/common/placeholders";
import { AWS_ACCOUNT_ID_REGEX } from "@/components/environments/environment-base";

interface PlaceholderControlProps {
	type: PlaceholderValueType;
	// Injected by the wrapping Form.Item.
	id?: string;
	value?: PlaceholderValue;
	checked?: boolean;
	onChange?: (value: unknown) => void;
}

/** The input control for one placeholder type. `aws-region` and `aws-account-id` are
 * native types with a real picker/validated input instead of a plain text box — add
 * the next native type here as its own case. */
export function PlaceholderControl({
	type,
	id,
	value,
	checked,
	onChange,
}: PlaceholderControlProps) {
	switch (type) {
		case "number":
			return (
				<InputNumber
					id={id}
					value={value as number | undefined}
					onChange={onChange}
					style={{ width: "100%" }}
				/>
			);
		case "boolean":
			return (
				<Switch size="small" id={id} checked={checked} onChange={onChange} />
			);
		case "aws-region":
			return (
				<AwsRegionSelect
					id={id}
					value={value as string | undefined}
					onChange={onChange}
				/>
			);
		case "aws-account-id":
			return (
				<Input
					id={id}
					value={value as string | undefined}
					onChange={onChange}
					maxLength={12}
					placeholder="123456789012"
				/>
			);
		default:
			return (
				<Input
					id={id}
					value={value as string | undefined}
					onChange={onChange}
				/>
			);
	}
}

interface PlaceholderFormItemProps {
	field: PlaceholderField;
	/** Form field name; defaults to the placeholder's key. */
	name?: string;
	style?: CSSProperties;
}

/** A labelled, validated form item asking for one placeholder's value. */
export function PlaceholderFormItem({
	field,
	name,
	style,
}: PlaceholderFormItemProps) {
	const isBoolean = field.type === "boolean";
	return (
		<Form.Item
			name={name ?? field.key}
			label={field.label}
			extra={field.description}
			// A toggle always has a value; starting it at false keeps "required" satisfiable.
			initialValue={field.default ?? (isBoolean ? false : undefined)}
			valuePropName={isBoolean ? "checked" : "value"}
			rules={[
				{ required: field.required !== false, message: `Enter ${field.label}` },
				...(field.type === "aws-account-id"
					? [
							{
								pattern: AWS_ACCOUNT_ID_REGEX,
								message: "Enter a 12-digit AWS account ID",
							},
						]
					: []),
			]}
			style={style}
		>
			<PlaceholderControl type={field.type} />
		</Form.Item>
	);
}
